import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  UserCog, Plus, Loader2, CheckCircle2, X, AlertTriangle,
  Shield, Search, Pencil, Trash2, KeyRound, Eye, EyeOff
} from "lucide-react";
import axios from "axios";
import { API, useAuth } from "../contexts/AuthContext";
import { formatDate } from "../utils/format";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { ROLES, ROLE_GROUPS } from "../constants/roles";
import RoleConfigPanel from "../components/RoleConfigPanel";

// ==================== ROLES (Bloque 12 — fuente canónica compartida) ====================
// Ver /app/frontend/src/constants/roles.js

function getRoleColor(role) {
  return ROLES.find(r => r.value === role)?.color || "bg-gray-100 text-gray-600";
}

// ==================== ROLE SELECT GROUPED ====================
function RoleSelect({ value, onChange, roles = ROLES, className = "" }) {
  // Fix GU (2026-10-17): el rol Superadministrador solo lo asigna otro Superadministrador.
  const { user } = useAuth();
  const conSuper = user?.role === "Superadministrador" || value === "Superadministrador";
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className={`w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4] bg-white ${className}`}
      data-testid="user-role-select"
    >
      <option value="" disabled>Seleccionar rol...</option>
      {value && !roles.some(r => r.value === value) && <option value={value}>{value} (actual)</option>}
      {[...ROLE_GROUPS, "Personalizados"].map(group => {
        const groupRoles = roles.filter(r => r.group === group && (conSuper || r.value !== "Superadministrador"));
        return (
          <optgroup key={group} label={`— ${group} —`}>
            {groupRoles.map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </optgroup>
        );
      })}
    </select>
  );
}

// ==================== NEW USER MODAL ====================
function NewUserModal({ open, onClose, onSaved, roles }) {
  const [form, setForm] = useState({ email: "", name: "", role: "", password: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showPw, setShowPw] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.name || !form.password) { setError("Complete todos los campos"); return; }
    if (!form.role) { setError("Seleccione un rol"); return; }
    if (form.password.length < 6) { setError("La contraseña debe tener al menos 6 caracteres"); return; }
    setSaving(true); setError("");
    try {
      await axios.post(`${API}/settings/users`, form, { withCredentials: true });
      toast.success(`Usuario ${form.name} creado con rol ${form.role}`);
      setForm({ email: "", name: "", role: "", password: "" });
      onSaved(); onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Error al crear usuario");
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md" data-testid="new-user-modal">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Nuevo Usuario</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100"><X size={18} className="text-gray-500" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Nombre y Apellido *</label>
            <input name="name" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
              placeholder="Nombre completo del usuario"
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4]"
              data-testid="user-name-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Usuario (Email) *</label>
            <input name="email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
              placeholder="usuario@cen.com.py"
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4]"
              data-testid="user-email-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Contraseña *</label>
            <div className="relative">
              <input name="password" type={showPw ? "text" : "password"} value={form.password}
                onChange={e => setForm({...form, password: e.target.value})}
                placeholder="Mínimo 6 caracteres"
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 pr-10 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4]"
                data-testid="user-password-input" />
              <button type="button" onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Rol *</label>
            <RoleSelect roles={roles} value={form.role} onChange={v => setForm({...form, role: v})} />
            <p className="text-xs text-gray-400 mt-1">{ROLES.find(r => r.value === form.role)?.desc}</p>
          </div>
          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2 text-sm">
              <AlertTriangle size={14} />{error}
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">
              Cancelar
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="save-user-btn">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
              {saving ? "Creando..." : "Crear Usuario"}
            </button>
          </div>
        </form>
      </div>
    </div>
  , document.body);
}

// ==================== EDIT USER MODAL ====================
function EditUserModal({ user: editUser, open, onClose, onSaved, roles, focusPassword = false }) {
  const { user: currentUser } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", role: "", newPassword: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showPw, setShowPw] = useState(false);
  const pwRef = useState(null);

  useEffect(() => {
    if (editUser) {
      setForm({ name: editUser.name || "", email: editUser.email || "", role: editUser.role || "", newPassword: "" });
      setError("");
      if (focusPassword) {
        setTimeout(() => {
          const el = document.querySelector('[data-testid="edit-user-password"]');
          if (el) { el.focus(); el.scrollIntoView({ behavior: "smooth", block: "center" }); }
        }, 200);
      }
    }
  }, [editUser, focusPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { setError("Nombre y email son obligatorios"); return; }
    if (form.newPassword && form.newPassword.length < 6) { setError("La contraseña debe tener al menos 6 caracteres"); return; }
    setSaving(true); setError("");
    try {
      const payload = { name: form.name, email: form.email, role: form.role };
      if (form.newPassword) payload.password = form.newPassword;
      await axios.put(`${API}/settings/users/${editUser._id}`, payload, { withCredentials: true });
      toast.success(`Usuario ${form.name} actualizado`);
      onSaved(); onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Error al actualizar usuario");
    } finally { setSaving(false); }
  };

  if (!open || !editUser) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md" data-testid="edit-user-modal">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Editar Usuario</h2>
            <p className="text-xs text-gray-400 mt-0.5">{editUser.email}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100"><X size={18} className="text-gray-500" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Nombre y Apellido *</label>
            <input value={form.name} onChange={e => setForm({...form, name: e.target.value})}
              placeholder="Nombre completo"
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4]"
              data-testid="edit-user-name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Usuario (Email) *</label>
            <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
              placeholder="usuario@cen.com.py"
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4]"
              data-testid="edit-user-email" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Nueva Contraseña <span className="text-gray-400 font-normal">(dejar vacío para no cambiar)</span></label>
            <div className="relative">
              <input type={showPw ? "text" : "password"} value={form.newPassword}
                onChange={e => setForm({...form, newPassword: e.target.value})}
                placeholder="Nueva contraseña (mín. 6 caracteres)"
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 pr-10 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4]"
                data-testid="edit-user-password" />
              <button type="button" onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Rol *</label>
            <RoleSelect roles={roles} value={form.role} onChange={v => setForm({...form, role: v})} />
            <p className="text-xs text-gray-400 mt-1">{ROLES.find(r => r.value === form.role)?.desc}</p>
          </div>
          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2 text-sm">
              <AlertTriangle size={14} />{error}
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">
              Cancelar
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="save-edit-user-btn">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
              {saving ? "Guardando..." : "Guardar Cambios"}
            </button>
          </div>
        </form>
      </div>
    </div>
  , document.body);
}

// ==================== MAIN PAGE ====================
export default function UsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [roleCatalogue, setRoleCatalogue] = useState(ROLES);
  const rolesChanged = (rows) => {
    setRoleCatalogue(rows.map(r => ROLES.find(base => base.value === r.value) || { ...r, group: "Personalizados", color: "bg-gray-100 text-gray-600", desc: r.custom ? "Consulta de módulos habilitados" : "Rol histórico" }));
  };
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editFocusPw, setEditFocusPw] = useState(false);
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("");

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/settings/users`, { withCredentials: true });
      setUsers(data);
    } catch (err) {
      toast.error("Error cargando usuarios");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleToggle = async (userId) => {
    try {
      const { data } = await axios.put(`${API}/settings/users/${userId}/toggle`, {}, { withCredentials: true });
      toast.success(data.is_active ? "Usuario activado" : "Usuario desactivado");
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`¿Eliminar definitivamente a ${u.name}? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/settings/users/${u._id}`, { withCredentials: true });
      toast.success(`Usuario ${u.name} eliminado`);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al eliminar"); }
  };

  const filteredUsers = users.filter(u => {
    const matchSearch = (
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase()) ||
      u.role?.toLowerCase().includes(search.toLowerCase())
    );
    const matchRole = !filterRole || u.role === filterRole;
    return matchSearch && matchRole;
  });

  // Group by role for stats
  const byRole = {};
  roleCatalogue.forEach(r => { byRole[r.value] = users.filter(u => u.role === r.value).length; });

  const isSuperAdmin = user?.role === "Superadministrador";
  const isAdminRole = user?.role === "Administrador";
  // Fix GU (2026-10-17): "que los perfiles de 'Dirección general' y 'Administración' sean los que puedan
  // editar (…) lo de los usuarios" (CEN). Dirección General administra usuarios como Administración.
  const isDirector = user?.role === "Director";
  const isAdmin = isAdminRole || isSuperAdmin || isDirector;
  const canEdit = (u) => isSuperAdmin || u.role !== "Superadministrador";   // Fix GU

  const canDelete = (u) => {
    if (u._id === user?._id) return false;
    if (isSuperAdmin) return true;
    if (isAdminRole || isDirector) return !["Superadministrador", "Administrador"].includes(u.role);   // Fix GU: + Director
    return false;
  };

  if (!isAdmin) {
    return (
      <div className="p-8 text-center">
        <Shield size={48} className="mx-auto text-gray-300 mb-3" />
        <p className="text-gray-500 font-medium">Acceso restringido</p>
        <p className="text-sm text-gray-400 mt-1">Solo administradores pueden gestionar usuarios</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Gestión de Usuarios</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {users.length} usuario{users.length !== 1 ? "s" : ""} en el sistema &middot; {roleCatalogue.length} roles disponibles
          </p>
        </div>
        <button onClick={() => setShowNew(true)}
          className="flex items-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-4 py-2.5 rounded-xl text-sm font-semibold shadow-sm self-start"
          data-testid="new-user-btn">
          <Plus size={16} /> Nuevo Usuario
        </button>
      </div>

      <RoleConfigPanel onChanged={rolesChanged} />

      {/* Roles Grid */}
      <div className="bg-white rounded-xl border border-gray-200/60 p-5 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Shield size={16} className="text-[#2E6DA4]" /> Roles del Sistema
        </h3>
        {[...ROLE_GROUPS, "Personalizados"].map(group => {
          const groupRoles = roleCatalogue.filter(r => r.group === group);
          return (
            <div key={group} className="mb-4 last:mb-0">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">{group}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                {groupRoles.map(r => (
                  <button key={r.value}
                    onClick={() => setFilterRole(filterRole === r.value ? "" : r.value)}
                    className={`rounded-lg p-2.5 border text-left transition-all hover:shadow-sm ${filterRole === r.value ? "border-[#2E6DA4] ring-1 ring-[#2E6DA4]/30 bg-blue-50" : "border-gray-100 bg-gray-50"}`}
                    data-testid={`role-filter-${r.value.replace(/\s/g, "-")}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${r.color}`}>{r.label}</span>
                      <span className="text-xs font-bold text-gray-500">{byRole[r.value] || 0}</span>
                    </div>
                    <p className="text-xs text-gray-400 leading-tight">{r.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Search & Filter bar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nombre, email o rol..."
            className="w-full border border-gray-300 rounded-xl pl-9 pr-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4]"
            data-testid="user-search"
          />
        </div>
        {filterRole && (
          <button onClick={() => setFilterRole("")}
            className="flex items-center gap-2 text-sm text-[#2E6DA4] border border-[#2E6DA4]/30 bg-blue-50 px-3 py-2 rounded-xl hover:bg-blue-100">
            <X size={14} /> Filtro: {filterRole}
          </button>
        )}
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="space-y-3">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-16" />)}</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm overflow-hidden" data-testid="users-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50/80 border-b border-gray-100">
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Nombre y Apellido</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">Usuario (Email)</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Rol</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Estado</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden lg:table-cell">Creado</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredUsers.map((u) => (
                <tr key={u._id} className="hover:bg-gray-50/50 transition-colors" data-testid="user-row">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-full bg-[#2E6DA4]/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-[#2E6DA4] text-sm font-bold">{u.name?.charAt(0)?.toUpperCase() || "?"}</span>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">{u.name}</p>
                        <p className="text-xs text-gray-400 sm:hidden">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-gray-500 hidden sm:table-cell text-xs font-mono">{u.email}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-0.5 rounded-full font-semibold ${getRoleColor(u.role)}`}>
                      <Shield size={9} />{u.role}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold ${u.is_active !== false ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                      {u.is_active !== false ? "Activo" : "Inactivo"}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-gray-500 text-xs hidden lg:table-cell">{formatDate(u.created_at)}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {canEdit(u) && (<>{/* Fix GU: a un Superadministrador solo lo modifica otro */}
                      {/* Edit button */}
                      <button
                        onClick={() => { setEditUser(u); setEditFocusPw(false); }}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-[#2E6DA4] hover:bg-blue-50 transition-colors"
                        title="Editar usuario"
                        data-testid="edit-user-btn">
                        <Pencil size={14} />
                      </button>
                      {/* Reset password shortcut */}
                      <button
                        onClick={() => { setEditUser(u); setEditFocusPw(true); }}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                        title="Cambiar contraseña"
                        data-testid="change-pw-btn">
                        <KeyRound size={14} />
                      </button>
                      {/* Toggle active */}
                      <button
                        onClick={() => handleToggle(u._id)}
                        className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-all ${u.is_active !== false ? "text-red-600 hover:bg-red-50" : "text-emerald-600 hover:bg-emerald-50"}`}
                        data-testid="toggle-user-btn">
                        {u.is_active !== false ? "Desactivar" : "Activar"}
                      </button>
                      </>)}
                      {/* Delete — Superadmin puede eliminar a cualquiera (excepto sí mismo);
                          Administrador solo a roles operativos (no Superadmin/Admin) */}
                      {canDelete(u) && (
                        <button
                          onClick={() => handleDelete(u)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          title="Eliminar usuario"
                          data-testid="delete-user-btn">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredUsers.length === 0 && (
            <div className="p-10 text-center text-gray-400">
              <UserCog size={36} className="mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No se encontraron usuarios</p>
              {(search || filterRole) && (
                <p className="text-sm mt-1">Prueba con otros filtros o <button onClick={() => { setSearch(""); setFilterRole(""); }} className="text-[#2E6DA4] underline">limpiar búsqueda</button></p>
              )}
            </div>
          )}
          <div className="px-5 py-3 border-t border-gray-50 bg-gray-50/50">
            <p className="text-xs text-gray-400">{filteredUsers.length} de {users.length} usuarios mostrados</p>
          </div>
        </div>
      )}

      <NewUserModal roles={roleCatalogue} open={showNew} onClose={() => setShowNew(false)} onSaved={fetchUsers} />
      <EditUserModal roles={roleCatalogue} user={editUser} open={!!editUser} onClose={() => { setEditUser(null); setEditFocusPw(false); }} onSaved={fetchUsers} focusPassword={editFocusPw} />
    </div>
  );
}
